'use client';
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from 'react';

interface ListingContextType {
  productsToDisplay: any[];
  setProductsToDisplay: (products: any[]) => void;
  scrollPosition: number;
  setScrollPosition: (position: number) => void;
  lastViewedProductId: string | null;
  setLastViewedProductId: (id: string | null) => void;
  shouldRestoreScroll: boolean;
  setShouldRestoreScroll: (value: boolean) => void;
}

const Context = createContext<ListingContextType | undefined>(undefined);

const ListingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [productsToDisplay, setProductsToDisplay] = useState<any[]>([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [lastViewedProductId, setLastViewedProductId] = useState<string | null>(
    null
  );
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced scroll handler to prevent flickering
  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        setScrollPosition(window.scrollY);
      }, 100); // Debounce time of 100ms
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Save scroll position before unmount
  useEffect(() => {
    return () => {
      setScrollPosition(window.scrollY);
    };
  }, []);

  return (
    <Context.Provider
      value={{
        productsToDisplay,
        setProductsToDisplay,
        scrollPosition,
        setScrollPosition,
        lastViewedProductId,
        setLastViewedProductId,
        shouldRestoreScroll,
        setShouldRestoreScroll,
      }}>
      {children}
    </Context.Provider>
  );
};

export const useListing = (): ListingContextType => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useListing must be used within a ListingProvider');
  }
  return context;
};

export default ListingProvider;
